import React from 'react';
import { X } from 'lucide-react';

interface CourierAgreementModalProps {
  onClose: () => void;
}

const CourierAgreementModal: React.FC<CourierAgreementModalProps> = ({ onClose }) => {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-5"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-logo-green rounded-t-xl">
          <h2 className="text-2xl font-bold text-white m-0">
            Valley Eats Courier Agreement
          </h2>
          <button
            onClick={onClose}
            className="bg-white bg-opacity-20 border-none rounded-md p-2 cursor-pointer flex items-center justify-center text-white hover:bg-opacity-30 transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Agreement Content */}
        <div className="p-8 overflow-y-auto flex-1 leading-relaxed text-charcoal">
          <p className="mb-6 text-sm text-gray-600">
            THIS AGREEMENT is made as of the date set forth below by and between Valley Eats, (hereinafter the "Company")
            and the undersigned individual (hereinafter "You" or the "Contractor").
          </p>

          <p className="mb-6 font-semibold">
            FOR VALUABLE CONSIDERATION, the parties hereto agree as follows:
          </p>

          {/* Section 1 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              1. Independent Contractor Status
            </h3>
            <p className="m-0 text-[15px]">
              You are engaged by the Company as a non-exclusive independent contractor to provide delivery services
              (the "Services"). You may provide services to other businesses and consumers and hold yourself out as a
              separately established business.
            </p>
          </div>

          {/* Section 2 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              2. Termination
            </h3>
            <p className="m-0 text-[15px]">
              Either party may terminate this Agreement at any time by providing 7 days' written notice via email or other
              written communication. In case of a breach of this Agreement, the non-breaching party may terminate the Agreement
              with written notice. The Agreement will automatically terminate if no Services are provided for six (6) consecutive months.
            </p>
          </div>

          {/* Section 3 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              3. Service Flexibility
            </h3>
            <p className="m-0 text-[15px]">
              You have the flexibility to determine your availability, accept or refuse any delivery assignment, and are not
              required to follow specific routes or directions. The only expectation is that you comply with the Company's Code of Conduct.
            </p>
          </div>

          {/* Section 4 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              4. Use of Personnel
            </h3>
            <p className="m-0 text-[15px]">
              You may employ or engage personnel to perform the Services, provided that (i) you manage, supervise, and pay them,
              (ii) they agree to the terms of this Agreement, and (iii) you provide evidence of their agreement to the Company
              before they perform any Services.
            </p>
          </div>

          {/* Section 5 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              5. Business Judgment
            </h3>
            <p className="m-0 text-[15px]">
              You are responsible for using your own business judgment, which may lead to varying business outcomes, including
              potential profits or losses.
            </p>
          </div>

          {/* Section 6 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              6. Tools and Equipment
            </h3>
            <p className="m-0 text-[15px]">
              You are responsible for obtaining, maintaining, and using the necessary tools to perform the Services, which include:
            </p>
            <ul className="mt-2 mb-0 pl-6 list-disc">
              <li>A vehicle in good operating condition</li>
              <li>A smartphone with an operating system of iOS 10 or Android 5.0 or higher</li>
              <li>A thermal-grade food bag</li>
            </ul>
          </div>

          {/* Section 7 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              7. Independent Contractor Relationship
            </h3>
            <p className="m-0 text-[15px]">
              This Agreement does not create an employer-employee relationship. You are responsible for (i) taxes, insurance,
              permits, and licenses, and (ii) indemnifying the Company against any claims related to these obligations.
            </p>
          </div>

          {/* Section 8 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              8. Representations and Warranties
            </h3>
            <p className="mb-2 text-[15px]">You represent and warrant that:</p>
            <ul className="mt-0 mb-0 pl-6 list-disc">
              <li>You operate an independent business</li>
              <li>You hold a valid driver's license</li>
              <li>You own or have the right to operate a registered vehicle</li>
              <li>You maintain a liability insurance policy with minimum third-party limits of $2,000,000</li>
              <li>You comply with all applicable laws and regulations</li>
              <li>Your vehicle is in good operating condition</li>
              <li>You possess a smartphone with an operating system of iOS 10 or Android 5.0 or higher</li>
              <li>You are solely responsible for any liability arising from the operation of your vehicle</li>
              <li>You will provide proof of identity and consent to background checks when requested by the Company</li>
            </ul>
          </div>

          {/* Section 9 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              9. Compliance Documentation
            </h3>
            <p className="m-0 text-[15px]">
              You agree to provide documentation demonstrating compliance with these representations before performing any Services.
              If your compliance status changes, you must immediately notify the Company and provide updated, valid documents to
              avoid suspension of your Services.
            </p>
          </div>

          {/* Section 10 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              10. Notification of Non-Compliance
            </h3>
            <p className="m-0 text-[15px]">
              You must notify the Company immediately if you no longer meet any requirement in Section 8. Failure to do so may
              result in suspension of your Services until valid documents are provided.
            </p>
          </div>

          {/* Section 11 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              11. Performance Standards
            </h3>
            <p className="m-0 text-[15px]">
              You agree to perform Services in a safe, professional, and competent manner and comply with the Company's Code of
              Conduct and the Terms of Use located at{' '}
              <a href="https://valleyeats.ca/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-logo-green hover:underline">
                https://valleyeats.ca/terms-of-service
              </a>
              . In the event of a conflict between this Agreement and the Terms of Use, the provision offering the most protection
              to the Company will govern.
            </p>
          </div>

          {/* Section 12 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              12. Compensation
            </h3>
            <p className="m-0 text-[15px]">
              The Company will pay you for Services rendered on a weekly basis, with payments remitted on Fridays for the previous
              week's completed orders (allow up to 3 business days for processing). All payments are inclusive of applicable taxes,
              and you are responsible for remitting these taxes.
            </p>
          </div>

          {/* Section 13 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              13. Confidentiality
            </h3>
            <p className="m-0 text-[15px]">
              You agree to maintain the confidentiality of any Confidential Information related to the Company and its affiliates.
            </p>
          </div>

          {/* Section 14 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              14. Personal Information
            </h3>
            <p className="m-0 text-[15px]">
              You agree to use Personal Information solely for providing Services, maintain strict confidentiality, and securely
              destroy Personal Information after use. You must also comply with the Company's Privacy Policy located at{' '}
              <a href="https://www.valleyeats.ca/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-logo-green hover:underline">
                https://www.valleyeats.ca/privacy-policy
              </a>
              .
            </p>
          </div>

          {/* Section 15 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              15. Intellectual Property
            </h3>
            <p className="m-0 text-[15px]">
              All Company products, services, and intellectual property remain the property of the Company or its licensors.
            </p>
          </div>

          {/* Section 16 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              16. Disclaimer and Indemnification
            </h3>
            <p className="m-0 text-[15px]">
              The Company is not liable for delays or issues related to the Services. You agree to defend, indemnify, and hold
              the Company harmless from any claims arising from your breach of this Agreement or your vehicle operation during Services.
            </p>
          </div>

          {/* Section 17 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              17. Dispute Resolution
            </h3>
            <p className="m-0 text-[15px]">
              Disputes will first be resolved through informal discussions. If unresolved after 20 business days, disputes will
              be resolved through confidential arbitration under the ADR Institute of Canada, Inc. rules. Class action lawsuits
              are not permitted.
            </p>
          </div>

          {/* Section 18 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              18. Governing Law
            </h3>
            <p className="m-0 text-[15px]">
              This Agreement is governed by the laws of Ontario. Amendments take effect upon posting updates by the Company, and
              continued provision of Services constitutes your acceptance of the revised terms.
            </p>
          </div>

          {/* Section 19 */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-logo-green mb-3">
              19. Entire Agreement
            </h3>
            <p className="m-0 text-[15px]">
              This Agreement constitutes the entire understanding between the parties and supersedes any previous agreements or
              understandings, whether written or oral.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-logo-green text-white border-none rounded-lg text-sm font-semibold cursor-pointer hover:bg-shadow-green transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourierAgreementModal;
